import React, { useCallback, useEffect, useRef } from 'react';
import { useHistory, useTranslation } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { EventGroupCodeBanner } from './EventGroupCodeBanner';
import { EventGroupCodeBannerSlot } from './EventGroupCodeBannerSlot';
import { useEventGroupCodeBannerEligibility } from './useEventGroupCodeBannerEligibility';

export type EventGroupCodeBannerContainerProps = Readonly<{
  eventHandle: string;
  event: GuestSiteEvent;
  /** Rendered in the banner's place once the event is confirmed not eligible; nothing renders while that is still resolving. */
  fallback?: React.ReactNode;
}>;

/**
 * Connects the presentational {@link EventGroupCodeBanner} to guest-site data: it resolves per-guest
 * eligibility, builds the localized copy with the code interpolated, fires impression/click
 * telemetry, and navigates to accommodations.
 *
 * Renders nothing until eligibility is confirmed, then mounts {@link EventGroupCodeBannerSlot} and the
 * banner together: the slot takes the banner's height in flow and the `position: fixed` banner lands
 * on it, so the banner never covers content. Reserving the space speculatively (before the
 * eligibility reads land) is deliberately avoided — on ineligible events it painted a visible strip
 * that then collapsed (PRNT-4836 follow-up).
 */
export const EventGroupCodeBannerContainer: React.FC<EventGroupCodeBannerContainerProps> = ({ eventHandle, event, fallback }) => {
  const { tq } = useTranslation('guestSiteTravel');
  const telemetry = useGuestSiteTelemetry();
  const history = useHistory();

  const eventGroupCode = event.info.eventGroupCode;
  const { isEligible, isResolved, accommodationsPath, bannerUtm } = useEventGroupCodeBannerEligibility({
    eventId: event.id,
    eventHandle,
    eventGroupCode,
  });

  // event_service TRAV-4400: bucketed by JoyEvent (this event's firebaseId), matching
  // event_service's own evaluation of the same flag, so this banner and the backend-authored
  // accommodations note never disagree about which variant this event is in.
  // Skipped until the banner is actually eligible to render — otherwise every guest-site mount
  // would register an Amplitude exposure, including events with no code/hotel/RSVP that never see
  // either copy variant, corrupting the experiment (joy-crit).
  const [isNoteGuestTrackingTreatment, isLoadingNoteGuestTrackingFlag] = useIsFeatureEnabled('accommodationsNoteCopies', {
    skip: !isEligible || !eventGroupCode,
  });

  // Not ready until the flag has resolved — otherwise the banner briefly renders the control copy
  // and logs a view for it before flipping to the correct variant a moment later (joy-crit).
  const isReady = isEligible && !!eventGroupCode && !isLoadingNoteGuestTrackingFlag;

  // Fire the impression once, the first time the banner becomes ready to render.
  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (isReady && !hasTrackedView.current) {
      hasTrackedView.current = true;
      telemetry.weddingCodeBannerViewed(eventGroupCode);
    }
  }, [isReady, eventGroupCode, telemetry]);

  const handleNavigate = useCallback(() => {
    if (eventGroupCode) {
      // Same UTM as the path below, so the click event and the resulting page-view agree.
      telemetry.weddingCodeBannerClicked(eventGroupCode, bannerUtm);
    }
    // In-SPA navigation (accommodations is a guest-site route): keeps the SPA intact and lets the
    // click telemetry above flush, instead of being dropped by a full-page unload.
    history.push(accommodationsPath);
  }, [eventGroupCode, telemetry, bannerUtm, accommodationsPath, history]);

  if (isResolved && !(isEligible && eventGroupCode)) {
    return <>{fallback ?? null}</>;
  }

  if (!isReady) {
    return null;
  }

  // Banner copy from the Figma design (node 54145-35770). The code renders in the canonical
  // `-AND-` format (e.g. EMMY-AND-JOHN) — the Figma's `EMMA-RYAN` is a placeholder.
  // Two full sentences (not runtime concatenation) mirror event_service's own noteTreatment /
  // noteTreatmentGuestTracking split — the translation pipeline wants whole sentences, not a
  // fragment spliced into an existing one.
  const message = isNoteGuestTrackingTreatment
    ? tq`🏨 The couple has arranged guest lodging through Joy. Please book your room using group code ${eventGroupCode}. Booking through our site allows us to know where everyone is staying to help with our planning.`
    : tq`🏨 The couple has arranged guest lodging through Joy. Please book your room using group code ${eventGroupCode}.`;
  // Concise accessible name for the interactive banner — screen readers announce this instead of the
  // full marketing sentence.
  const ariaLabel = tq`Book your room with group code ${eventGroupCode}`;

  return (
    <>
      <EventGroupCodeBannerSlot message={message} />
      <EventGroupCodeBanner
        message={message}
        ariaLabel={ariaLabel}
        onNavigate={handleNavigate}
      />
    </>
  );
};

EventGroupCodeBannerContainer.displayName = 'EventGroupCodeBannerContainer';
